import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Vote, Loader2, Users, Building2, BarChart3, CheckCircle2,
  Info, TrendingUp, Search, ExternalLink, Trophy, MapPin,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie,
} from 'recharts';
import BrazilElectionMap from '../components/BrazilElectionMap';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#006030', '#F0D000', '#C62828', '#1565C0', '#6A1B9A', '#00838F', '#E65100', '#2E7D32', '#F57F17', '#4E342E'];

// Brazilian states (UFs) for the elected filter
const UF_LIST = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN',
  'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
];

// Cargo options: adapt list based on year type (municipal vs. general)
const CARGOS_MUNICIPAL = [
  { code: 11, label: 'Prefeito' },
  { code: 13, label: 'Vereador' },
];
const CARGOS_GERAL = [
  { code: 1, label: 'Presidente' },
  { code: 3, label: 'Governador' },
  { code: 5, label: 'Senador' },
  { code: 6, label: 'Deputado Federal' },
  { code: 7, label: 'Deputado Estadual' },
];

// Most common Brazilian parties (TSE sigla)
const PARTIDOS = [
  'PT', 'PL', 'MDB', 'PSDB', 'UNIÃO', 'PP', 'PSD', 'REPUBLICANOS', 'PDT', 'PSB',
  'PODEMOS', 'NOVO', 'PCdoB', 'PV', 'SOLIDARIEDADE', 'PSOL', 'AVANTE', 'CIDADANIA',
  'PSC', 'PMN', 'AGIR', 'PRD', 'REDE', 'DC',
];

const Elections = () => {
  const [year, setYear] = useState('2024');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // ─── Eleitos por Partido — filtros & estado ─────────────────
  const [electedState, setElectedState] = useState('');
  const [electedMuni, setElectedMuni] = useState('');
  const [electedCargo, setElectedCargo] = useState('0');
  const [electedParty, setElectedParty] = useState('');
  const [munisList, setMunisList] = useState([]);
  const [munisLoading, setMunisLoading] = useState(false);
  const [electedData, setElectedData] = useState(null);
  const [electedLoading, setElectedLoading] = useState(false);
  const [electedError, setElectedError] = useState('');

  const isMunicipalYear = ['2024', '2020'].includes(year);
  const cargoOptions = isMunicipalYear ? CARGOS_MUNICIPAL : CARGOS_GERAL;

  const loadData = async (y) => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/elections/summary`, { params: { year: parseInt(y) }, timeout: 60000 });
      setData(resp.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(year); }, [year]);

  // Reset filters when year type changes (municipal <-> geral)
  useEffect(() => {
    setElectedCargo('0');
    setElectedMuni('');
    setMunisList([]);
    setElectedData(null);
    setElectedError('');
  }, [isMunicipalYear]);

  // Fetch municipalities when state is selected (only for municipal years)
  useEffect(() => {
    const fetchMunis = async () => {
      if (!electedState || !isMunicipalYear) { setMunisList([]); return; }
      setMunisLoading(true);
      try {
        const resp = await axios.get(`${API}/electoral/municipalities`, {
          params: { state: electedState, year: parseInt(year) },
          timeout: 30000,
        });
        setMunisList(resp.data?.municipalities || []);
      } catch {
        setMunisList([]);
      } finally {
        setMunisLoading(false);
      }
    };
    fetchMunis();
    setElectedMuni('');
  }, [electedState, year, isMunicipalYear]);

  const consultarEleitos = async () => {
    setElectedLoading(true);
    setElectedError('');
    setElectedData(null);
    try {
      const params = { year: parseInt(year) };
      const stateVal = (electedState || '').trim();
      const partyVal = (electedParty || '').trim();
      if (stateVal && stateVal !== 'BR') params.state = stateVal;
      if (electedMuni) params.municipality = electedMuni;
      if (electedCargo && electedCargo !== '0') params.cargo = parseInt(electedCargo);
      if (partyVal) params.party = partyVal;

      const resp = await axios.get(`${API}/elections/elected`, { params, timeout: 120000 });
      setElectedData(resp.data);
      if (resp.data?.message && (!resp.data?.elected || resp.data.elected.length === 0)) {
        setElectedError(resp.data.message);
      }
    } catch (e) {
      setElectedError('Erro ao consultar o TSE. Tente novamente em instantes.');
    } finally {
      setElectedLoading(false);
    }
  };

  const s = data?.summary || {};
  const cargoData = (s.por_cargo || []).slice(0, 10).map(c => ({ name: c.cargo, value: c.total }));
  const partidoData = (s.por_partido || []).slice(0, 15).map(p => ({ name: p.partido, value: p.total }));
  const situacaoData = (s.por_situacao || []).map(st => ({ name: st.situacao, value: st.total }));

  return (
    <div className="space-y-6" data-testid="elections-page">
      <div className="text-center py-4 md:py-8">
        <h1 className="text-2xl sm:text-3xl lg:text-5xl font-black text-[#0F1C2E] tracking-tight mb-2" data-testid="elections-title">
          Eleições
        </h1>
        <p className="text-sm sm:text-base text-[#475569] max-w-2xl mx-auto px-2">
          Panorama eleitoral com dados oficiais do TSE: candidatos, partidos e resultados.
        </p>
      </div>

      <div className="flex justify-center">
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-[130px] h-10 border-[#E2E8F0]" data-testid="elections-year">
            <Vote className="w-4 h-4 mr-1 text-[#006030]" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2024, 2022, 2020, 2018].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[#006030] mr-3" />
          <span className="text-sm text-[#94A3B8]">Consultando dados do TSE...</span>
        </div>
      )}

      {!loading && data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="elections-stats">
            <Card className="p-4 bg-[#006030] text-white rounded-md text-center">
              <Users className="w-5 h-5 mx-auto mb-1 opacity-80" />
              <p className="text-2xl font-black">{(s.total_candidatos || 0).toLocaleString('pt-BR')}</p>
              <p className="text-[10px] opacity-70">Candidatos</p>
            </Card>
            <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md text-center">
              <CheckCircle2 className="w-5 h-5 mx-auto mb-1 text-[#2E7D32]" />
              <p className="text-2xl font-black text-[#2E7D32]">{(s.total_eleitos || 0).toLocaleString('pt-BR')}</p>
              <p className="text-[10px] text-[#94A3B8]">Eleitos</p>
            </Card>
            <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md text-center">
              <Building2 className="w-5 h-5 mx-auto mb-1 text-[#1565C0]" />
              <p className="text-2xl font-black text-[#0F1C2E]">{s.por_partido?.length || 0}</p>
              <p className="text-[10px] text-[#94A3B8]">Partidos</p>
            </Card>
            <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md text-center">
              <BarChart3 className="w-5 h-5 mx-auto mb-1 text-[#F57F17]" />
              <p className="text-2xl font-black text-[#0F1C2E]">{s.por_cargo?.length || 0}</p>
              <p className="text-[10px] text-[#94A3B8]">Cargos Disputados</p>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* By Cargo */}
            {cargoData.length > 0 && (
              <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md" data-testid="chart-cargo">
                <h3 className="text-sm font-bold text-[#0F1C2E] mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-[#006030]" /> Candidatos por Cargo</h3>
                <ResponsiveContainer width="100%" height={Math.max(180, cargoData.length * 32)}>
                  <BarChart data={cargoData} layout="vertical" margin={{ left: 5, right: 15 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#94A3B8' }} />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 10, fill: '#475569' }} />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 11 }} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={18}>
                      {cargoData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}

            {/* By Situação */}
            {situacaoData.length > 0 && (
              <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md" data-testid="chart-situacao">
                <h3 className="text-sm font-bold text-[#0F1C2E] mb-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#006030]" /> Situação das Candidaturas</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={situacaoData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={false} fontSize={10}>
                      {situacaoData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            )}
          </div>

          {/* By Partido */}
          {partidoData.length > 0 && (
            <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md" data-testid="chart-partido">
              <h3 className="text-sm font-bold text-[#0F1C2E] mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#006030]" /> Top 15: Candidatos por Partido</h3>
              <ResponsiveContainer width="100%" height={Math.max(200, partidoData.length * 28)}>
                <BarChart data={partidoData} layout="vertical" margin={{ left: 5, right: 15 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#94A3B8' }} />
                  <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10, fill: '#475569' }} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 11 }} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={16}>
                    {partidoData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* ─── NOVA SEÇÃO: Eleitos por Partido ─────────────────── */}
          <Card className="p-4 md:p-5 bg-white border border-[#E2E8F0] rounded-md" data-testid="elected-section">
            <div className="flex items-start justify-between gap-2 mb-3">
              <div>
                <h3 className="text-base md:text-lg font-bold text-[#0F1C2E] flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#F0D000]" />
                  Eleitos por Partido
                </h3>
                <p className="text-xs text-[#94A3B8] mt-0.5">
                  Consulta oficial ao TSE. Filtre por ano, cargo, estado, município e partido.
                </p>
              </div>
              <Badge className="bg-[#006030] text-white text-[10px] shrink-0">TSE oficial</Badge>
            </div>

            {/* Filtros */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
              {/* Cargo */}
              <Select value={electedCargo} onValueChange={setElectedCargo}>
                <SelectTrigger className="h-10 text-xs border-[#E2E8F0]" data-testid="elected-cargo">
                  <SelectValue placeholder="Cargo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Todos os cargos</SelectItem>
                  {cargoOptions.map(c => <SelectItem key={c.code} value={String(c.code)}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>

              {/* Estado */}
              <Select value={electedState} onValueChange={setElectedState}>
                <SelectTrigger className="h-10 text-xs border-[#E2E8F0]" data-testid="elected-state">
                  <SelectValue placeholder="Estado (UF)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BR">Federal / Todos</SelectItem>
                  {UF_LIST.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                </SelectContent>
              </Select>

              {/* Município (só para municipal) */}
              <Select
                value={electedMuni}
                onValueChange={setElectedMuni}
                disabled={!isMunicipalYear || !electedState || electedState === 'BR' || munisLoading}
              >
                <SelectTrigger className="h-10 text-xs border-[#E2E8F0]" data-testid="elected-muni">
                  <SelectValue placeholder={
                    !isMunicipalYear ? 'N/A (eleição geral)'
                    : munisLoading ? 'Carregando...'
                    : (!electedState || electedState === 'BR') ? 'Selecione UF'
                    : 'Município'
                  } />
                </SelectTrigger>
                <SelectContent>
                  {munisList.map(m => <SelectItem key={m.code} value={m.code}>{m.nome}</SelectItem>)}
                </SelectContent>
              </Select>

              {/* Partido */}
              <Select value={electedParty} onValueChange={setElectedParty}>
                <SelectTrigger className="h-10 text-xs border-[#E2E8F0]" data-testid="elected-party">
                  <SelectValue placeholder="Partido" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Todos os partidos</SelectItem>
                  {PARTIDOS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>

              {/* Botão */}
              <Button
                onClick={consultarEleitos}
                disabled={electedLoading}
                className="h-10 bg-[#006030] hover:bg-[#004D26] text-white text-xs"
                data-testid="elected-search-btn"
              >
                {electedLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" /> Consultar</>}
              </Button>
            </div>

            {/* Mensagem de erro/validação */}
            {electedError && (
              <div className="flex items-start gap-2 p-3 bg-[#FFF8E1] border border-[#F0D000] rounded-md text-xs text-[#8C5A00]" data-testid="elected-error">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{electedError}</span>
              </div>
            )}

            {/* Loading state */}
            {electedLoading && (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-[#006030] mr-3" />
                <span className="text-sm text-[#94A3B8]">Consultando eleitos no TSE...</span>
              </div>
            )}

            {/* Resultados */}
            {!electedLoading && electedData && electedData.elected && electedData.elected.length > 0 && (
              <div data-testid="elected-results">
                <div className="flex items-center justify-between mb-2 mt-1">
                  <p className="text-xs text-[#475569] font-medium">
                    {electedData.total} eleito(s) encontrados
                    {electedData.truncated && <span className="text-[#F57F17]"> (amostra limitada)</span>}
                  </p>
                  <a
                    href={electedData.fonte_url || 'https://divulgacandcontas.tse.jus.br/'}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] text-[#006030] hover:underline flex items-center gap-1"
                  >
                    Fonte: {electedData.fonte} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                <div className="divide-y divide-[#E2E8F0] rounded-md border border-[#E2E8F0] overflow-hidden">
                  {electedData.elected.map((e, idx) => (
                    <div
                      key={e.id || idx}
                      className="flex items-center gap-3 p-3 hover:bg-[#F4F6F8] transition-colors"
                      data-testid={`elected-row-${idx}`}
                    >
                      {e.foto_url ? (
                        <img src={e.foto_url} alt={e.nome_urna} className="w-10 h-10 rounded-full object-cover border border-[#E2E8F0] shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#F0D000] text-[#0F1C2E] flex items-center justify-center font-bold text-xs shrink-0">
                          {(e.nome_urna || '?').slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-bold text-[#0F1C2E] truncate">{e.nome_urna}</p>
                          <Badge className="bg-[#006030] text-white text-[10px]">{e.partido || 'S/P'}</Badge>
                          <Badge className="bg-[#E8F5E9] text-[#2E7D32] text-[10px]">{e.situacao}</Badge>
                          {e.numero && <span className="text-[10px] text-[#94A3B8]">#{e.numero}</span>}
                        </div>
                        <p className="text-[11px] text-[#475569] truncate">
                          {e.cargo}
                          {(e.municipio_nome || e.estado) && (
                            <span className="text-[#94A3B8]"> · <MapPin className="w-3 h-3 inline" /> {e.municipio_nome || e.estado}</span>
                          )}
                          {e.votacao != null && (
                            <span className="text-[#006030] font-medium"> · {Number(e.votacao).toLocaleString('pt-BR')} votos</span>
                          )}
                        </p>
                      </div>
                      <a
                        href={e.tse_ref}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-[#006030] hover:underline flex items-center gap-1 shrink-0"
                        data-testid={`elected-tse-link-${idx}`}
                      >
                        TSE <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Estado inicial / empty */}
            {!electedLoading && !electedData && !electedError && (
              <div className="flex items-start gap-2 p-3 bg-[#F4F6F8] border border-[#E2E8F0] rounded-md text-xs text-[#94A3B8]">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Selecione os filtros e clique em <b>Consultar</b>. Dados oficiais diretos do TSE (DivulgaCandContas).</span>
              </div>
            )}
          </Card>

          {/* ─── NOVA SEÇÃO: Mapa Eleitoral ─────────────────────── */}
          <BrazilElectionMap year={year} />

          {/* Footer info */}
          <div className="flex items-start gap-2 p-3 bg-[#F4F6F8] border border-[#E2E8F0] rounded-md" data-testid="elections-footer">
            <Info className="w-4 h-4 text-[#94A3B8] shrink-0 mt-0.5" />
            <div className="text-[10px] text-[#94A3B8]">
              <p>Tipo: {data.tipo} | Estados consultados: {data.estados_consultados?.join(', ')}</p>
              <p>Fonte: {data.fonte} | Atualizado: {data.updated_at ? new Date(data.updated_at).toLocaleString('pt-BR') : 'N/A'}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Elections;
